import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Grid,
  Snackbar,
  Switch,
  Typography,
} from '@mui/material';
import {
  getDailyPageErrorMessage,
  getDailyPagePayload,
  listFeatureFlags,
  updateFeatureFlag,
} from '../../services/dailyPageApi';
import { DAILIES_FLAG_KEYS } from './dailyTasksUtils';

function DailiesFlagsPanel() {
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [error, setError] = useState('');
  const [flagsByKey, setFlagsByKey] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listFeatureFlags();
      if (res?.http_status && res.http_status >= 400) {
        setError(getDailyPageErrorMessage(res, 'Failed to load feature flags.'));
        return;
      }
      const list =
        getDailyPagePayload(res)?.flags ??
        res?.data?.flags ??
        (Array.isArray(res?.data) ? res.data : null) ??
        res?.flags ??
        [];
      if (!Array.isArray(list)) {
        setError('Failed to load feature flags.');
        return;
      }
      const map = {};
      list.forEach((f) => {
        if (f?.key) map[f.key] = f;
      });
      setFlagsByKey(map);
    } catch (err) {
      setError(err.message || 'Failed to load feature flags.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (key) => {
    const flag = flagsByKey[key];
    if (!flag) {
      setSnackbar({
        open: true,
        message: `Flag “${key}” is not on the server yet. Create it on the Feature Flags page.`,
        severity: 'warning',
      });
      return;
    }
    setToggling(key);
    try {
      const res = await updateFeatureFlag(key, { is_enabled: !flag.is_enabled });
      if (res?.http_status && res.http_status >= 400) {
        setSnackbar({
          open: true,
          message: getDailyPageErrorMessage(res, 'Failed to update flag.'),
          severity: 'error',
        });
        return;
      }
      setFlagsByKey((prev) => ({
        ...prev,
        [key]: { ...flag, is_enabled: !flag.is_enabled },
      }));
      setSnackbar({
        open: true,
        message: `${flag.name || key} ${!flag.is_enabled ? 'enabled' : 'disabled'}. Takes effect immediately.`,
        severity: 'success',
      });
    } finally {
      setToggling(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Quick toggles for Daily Tasks keys. Full CRUD lives on{' '}
        <Button
          component={Link}
          to="/admin/feature-flags"
          size="small"
          endIcon={<OpenInNewIcon fontSize="small" />}
          sx={{ textTransform: 'none' }}
        >
          Feature Flags
        </Button>
        .
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {DAILIES_FLAG_KEYS.map((item) => {
          const flag = flagsByKey[item.key];
          const enabled = !!flag?.is_enabled;
          return (
            <Grid item xs={12} md={6} key={item.key}>
              <Card variant="outlined">
                <CardContent
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Box>
                    <Typography variant="subtitle1">{item.label}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Key: <code>{item.key}</code>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.help}
                    </Typography>
                    {!flag && (
                      <Typography variant="caption" color="warning.main">
                        Not found — create on Feature Flags page
                      </Typography>
                    )}
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enabled}
                        onChange={() => handleToggle(item.key)}
                        disabled={!flag || toggling === item.key}
                        color="primary"
                      />
                    }
                    label={
                      toggling === item.key ? (
                        <CircularProgress size={18} />
                      ) : enabled ? (
                        'On'
                      ) : (
                        'Off'
                      )
                    }
                  />
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default DailiesFlagsPanel;
